// src/components/game/stages/AscentStage.jsx
import React, { useState, useRef } from 'react';
import ImmigrationQuiz from '../tasks/ImmigrationQuiz';
import { ASSETS } from '../../../utils/GameAssets';

// --- DATA ---
const COUNTRY_DATA = {
  Canada: { border: [{text:"Welcome to Canada. Program?",options:[{text:"Express Entry",correct:true},{text:"Job Search",correct:false}]}], consultant: {quizQuestion:"Fee?",options:[{text:"$1,525",correct:true}]} },
  UK: { border: [{text:"Purpose?",options:[{text:"Skilled Worker",correct:true},{text:"Looking",correct:false}]}], consultant: {quizQuestion:"Auth Doc?",options:[{text:"CoS",correct:true}]} },
  USA: { border: [{text:"Visa?",options:[{text:"H-1B",correct:true}]}], consultant: {quizQuestion:"Sponsor?",options:[{text:"Employer",correct:true}]} },
  Australia: { border: [{text:"Subclass?",options:[{text:"189",correct:true}]}], consultant: {quizQuestion:"Visa?",options:[{text:"189",correct:true}]} },
  NewZealand: { border: [{text:"Intent?",options:[{text:"Residency",correct:true}]}], consultant: {quizQuestion:"Criteria?",options:[{text:"Points",correct:true}]} }
};

const HR_VARIANTS = {
    Canada: [{text:"Credentials not recognized.", cost:"Underemployed"}],
    UK: [{text:"Missing UK ENIC.", cost:"Rejected"}],
    USA: [{text:"Lost Lottery.", cost:"Failed"}],
    Australia: [{text:"Skills expired.", cost:"Invalid"}],
    NewZealand: [{text:"Health check.", cost:"Medical Issue"}]
};

const BIDDING_ITEMS = [
    { id: 'form', label: "Standard Bid Form", valid: true, type: 'paperwork' },
    { id: 'bond', label: "Bid Security", valid: true, type: 'money' },
    { id: 'scope', label: "Scope of Work", valid: true, type: 'paperwork' },
    { id: 'financials', label: "Audited Financials", valid: true, type: 'paperwork' },
    { id: 'cash', label: "Envelope of Cash", valid: false, type: 'bribe' }, 
    { id: 'iou', label: "IOU Note", valid: false, type: 'garbage' }, 
    { id: 'verbal', label: "Verbal Agreement", valid: false, type: 'garbage' } 
];

// --- HELPERS ---
const SceneLayout = ({ video, title, children }) => (
    <div className="relative w-full h-full bg-black">
        <video src={video} autoPlay loop muted className="absolute inset-0 w-full h-full object-cover opacity-60" />
        <div className="relative z-10 p-10 h-full flex flex-col justify-end pb-20 items-center text-center">
            {title && <h2 className="text-3xl font-bold text-yellow-500 mb-6 bg-black/60 px-4 py-1">{title}</h2>}
            {children}
        </div>
    </div>
);

const VideoPlayer = ({ src, next }) => (
    <div className="w-full h-full bg-black flex items-center justify-center cursor-pointer" onClick={next}>
        <video src={src} autoPlay onEnded={next} className="w-full h-full object-cover" onError={next} />
        <div className="absolute bottom-10 right-10 text-xs text-gray-500 border border-gray-700 px-3 py-1 bg-black/50">Skip >></div>
    </div>
);

// --- MAIN COMPONENT ---
export function AscentStage({ dispatch, profession, integrity, country = 'Canada' }) {
  const [phase, setPhase] = useState('CHAPTER_VIDEO'); 
  const [bidSelection, setBidSelection] = useState([]);
  const [qIndex, setQIndex] = useState(0); 
  const [hrIndex, setHrIndex] = useState(0);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [outcomeCard, setOutcomeCard] = useState(null);
  
  // FIX: Corrected "cconst" to "const"
  const fateRef = useRef(Math.floor(Math.random() * 80) - 40);
  
  const getCountryData = () => COUNTRY_DATA[country] || COUNTRY_DATA['Canada'];
  const getHRData = () => HR_VARIANTS[country] || HR_VARIANTS['Canada'];

  const toggleBidItem = (item) => {
      if (bidSelection.includes(item)) setBidSelection(bidSelection.filter(i => i !== item));
      else if (bidSelection.length < 3) setBidSelection([...bidSelection, item]);
  };

  const submitBid = () => {
      setHasSubmitted(true);
      let bribeCount = 0;
      bidSelection.forEach(i => { if (i.type === 'bribe') bribeCount++; });

      if (bribeCount > 0) {
          dispatch({ type: 'UPDATE_STATS', payload: { integrity: -25, resources: 2000 } });
          fateRef.current += 30; 
      } else {
          dispatch({ type: 'UPDATE_STATS', payload: { integrity: 10, resources: 1000 } });
          fateRef.current -= 10; 
      }

      const score = integrity + fateRef.current;
      
      let img, title, desc;
      // Using ASSETS with correct keys
      if (score < -20) { img = ASSETS.OUTCOMES.WolfTicket; title = "Wolf's Ticket"; desc = "Exiled. Marked. Rejected."; }
      else if (score < 20) { img = ASSETS.OUTCOMES.Precariat; title = "The Precariat"; desc = "Drifting between survival and ruin."; }
      else if (score < 60) { img = ASSETS.OUTCOMES.ScrewSystem; title = "Screw of the System"; desc = "You turned when they said turn."; }
      else if (score < 100) { img = ASSETS.OUTCOMES.CandleEnds; title = "Burning Candle"; desc = "Success at a terrible physical cost."; }
      else { img = ASSETS.OUTCOMES.Recommended; title = "Recommended"; desc = "Doors open for you now. Respect earned."; }

      setOutcomeCard({ img, title, desc });
  };

  if (phase === 'CHAPTER_VIDEO') return <VideoPlayer src={ASSETS.CHAPTERS.Age2_Intro} next={() => setPhase('BORDER')} />;
  
  if (phase === 'BORDER') {
      const q = getCountryData().border[qIndex];
      return <SceneLayout video={ASSETS.NPCS.Officer} title={`Border Control (${country})`}><div className="bg-gray-900/90 p-6 rounded border-l-4 border-red-500 max-w-2xl text-left"><p className="text-xl mb-4 text-white">"{q.text}"</p>{q.options.map((opt, i) => (<button key={i} onClick={() => { if(!opt.correct) dispatch({type:'UPDATE_STATS',payload:{integrity:-5}}); if(qIndex+1<getCountryData().border.length) setQIndex(qIndex+1); else {setQIndex(0); setPhase('CONSULTANT');} }} className="block w-full text-left p-3 mb-2 bg-black text-gray-300 hover:text-white border border-gray-600">{opt.text}</button>))}</div></SceneLayout>;
  }

  if (phase === 'CONSULTANT') return <SceneLayout video={ASSETS.NPCS.Consultant} title="Immigration"><ImmigrationQuiz country={country} npcData={{dialogueTree: {[country]: getCountryData().consultant}}} onComplete={() => setPhase('HR')} /></SceneLayout>;

  if (phase === 'HR') {
      const dialog = getHRData()[0];
      return <SceneLayout video={ASSETS.NPCS.HR} title={`Human Resources (${country})`}><div className="bg-gray-900/90 p-6 rounded border-l-4 border-blue-500 max-w-2xl text-white"><p className="text-xl mb-6">"{dialog.text}"</p><p className="text-yellow-400 mb-6 italic">{dialog.cost}</p><button onClick={() => setPhase('FALSE_CEILING_VIDEO')} className="w-full py-4 bg-indigo-600 font-bold rounded">REALIZATION: START BUSINESS</button></div></SceneLayout>;
  }

  if (phase === 'FALSE_CEILING_VIDEO') return <VideoPlayer src={ASSETS.NARRATIVE.FalseCeiling} next={() => setPhase('REGISTRAR')} />;
  
  // FIX: HARDCODED OPTIONS (Prevents blank screen)
  if (phase === 'REGISTRAR') {
      const options = [
          {title: "Consulting", cost: 500, risk: "Low"}, 
          {title: "Logistics", cost: 2000, risk: "Medium"}, 
          {title: "Infrastructure", cost: 5000, risk: "High"}
      ];
      return (
      <SceneLayout video={ASSETS.NPCS.Registrar} title="Company Registrar">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-5xl">
              {options.map((opt, i) => (
                  <div key={i} className="bg-black/60 p-6 border border-gray-600 rounded hover:border-yellow-500">
                      <h3 className="font-bold text-white text-xl mb-2">{opt.title}</h3>
                      <div className="flex justify-between text-gray-400 text-sm mb-4"><span>${opt.cost}</span><span>{opt.risk} Risk</span></div>
                      <button onClick={() => setPhase('NEW_GAME_VIDEO')} className="w-full py-3 bg-blue-700 text-white rounded font-bold hover:bg-blue-600">Select</button>
                  </div>
              ))}
          </div>
      </SceneLayout>
      );
  }

  if (phase === 'NEW_GAME_VIDEO') return <VideoPlayer src={ASSETS.NARRATIVE.TheNewGame} next={() => setPhase('BIDDING')} />;

  if (phase === 'BIDDING') {
    if (hasSubmitted && outcomeCard) {
        return (
            <div className="h-full w-full bg-black flex items-center justify-center relative">
                <img src={outcomeCard.img} alt="Fate" className="absolute inset-0 w-full h-full object-cover opacity-40" />
                <div className="relative z-10 text-center p-8 bg-black/80 border-2 border-yellow-500 rounded-lg max-w-2xl">
                    <h2 className="text-3xl text-yellow-500 mb-4">{outcomeCard.title}</h2>
                    <p className="text-gray-300 mb-8">{outcomeCard.desc}</p>
                    <button onClick={() => dispatch({ type: 'ADVANCE_AGE', payload: 'Recalibration' })} className="bg-white text-black px-8 py-4 rounded font-bold hover:bg-gray-200">ENTER AGE 3</button>
                </div>
            </div>
        );
    }
    return (
      <SceneLayout video={ASSETS.NPCS.HeadOfBidding} title="Tender Committee">
        <div className="bg-gray-900/90 p-6 rounded border-2 border-green-500 max-w-4xl text-white">
            <h3 className="text-xl font-bold mb-4">"Assemble your Bid Package. Select 3 items."</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                {BIDDING_ITEMS.map((item) => {
                    const isSelected = bidSelection.includes(item);
                    return <button key={item.id} onClick={() => toggleBidItem(item)} className={`p-4 border rounded text-left transition-all ${isSelected ? 'bg-green-700 border-green-400' : 'bg-black/60 border-gray-600 hover:border-white'}`}><span className="block font-bold text-sm">{item.label}</span><span className="text-xs text-gray-400 uppercase">{item.type}</span></button>;
                })}
            </div>
            <button onClick={submitBid} disabled={bidSelection.length === 0} className={`w-full py-4 font-bold rounded text-xl ${bidSelection.length > 0 ? 'bg-blue-600 hover:bg-blue-500 text-white' : 'bg-gray-700 text-gray-500'}`}>SUBMIT PROPOSAL</button>
        </div>
      </SceneLayout>
    );
  }
  return <div>Loading...</div>;
}